/**
 * Column Visibility Menu
 *
 * Generic dropdown menu for managing column visibility and order.
 * Reusable across any datatable (tasks, matters, intake, etc.).
 *
 * Features:
 * - Checkbox list of all available columns
 * - Drag-and-drop reordering within visible columns
 * - Grouped by category
 * - Reset to defaults button
 *
 * Uses:
 * - Radix Popover (stays open on click, unlike Menu)
 * - dnd-kit for drag-drop reordering
 */

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ArrowPathIcon, ViewColumnsIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { ColumnGroup } from './columnUtils';
import { DraggableColumnItem } from './DraggableColumnItem';
import { TableColumn } from './types';

// ----------------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------------

/** Distance in pixels before drag activation */
const DRAG_ACTIVATION_DISTANCE = 5;

/** Maximum height for the scrollable menu content */
const MAX_MENU_HEIGHT = 'max-h-96';

/** Width of the popover menu */
const MENU_WIDTH = 'w-64';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

export interface ColumnVisibilityMenuProps<TData> {
  /** All available columns from the column factory */
  allColumns: TableColumn<TData>[];
  /** Currently visible column IDs in display order */
  visibleColumns: string[];
  /** Callback when a column is toggled */
  onToggleColumn: (columnId: string) => void;
  /** Callback when columns are reordered (receives new full order) */
  onReorderColumns: (newOrder: string[]) => void;
  /** Callback to reset to default layout */
  onReset: () => void;
  /** Whether the current layout differs from default */
  hasChanges: boolean;
  /**
   * Function to get display name for a column ID.
   * Falls back to column header (if string) or column ID.
   */
  getDisplayName?: (columnId: string) => string;
  /**
   * Function to group columns by category.
   * If not provided, all hidden columns appear in a flat list.
   */
  groupByCategory?: (columnIds: string[]) => ColumnGroup[];
  /**
   * Custom label for the trigger button.
   * @default "Manage columns"
   */
  label?: string;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export const ColumnVisibilityMenu = <TData,>({
  allColumns,
  visibleColumns,
  onToggleColumn,
  onReorderColumns,
  onReset,
  hasChanges,
  getDisplayName: customGetDisplayName,
  groupByCategory,
  label = 'Manage columns',
}: ColumnVisibilityMenuProps<TData>) => {
  // Set up dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: DRAG_ACTIVATION_DISTANCE,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Build a map of column ID -> column for quick lookup
  const columnMap = useMemo(
    () => new Map(allColumns.map((col) => [col.id, col])),
    [allColumns],
  );

  // Group all columns by category for the menu
  const allColumnIds = useMemo(
    () => allColumns.map((col) => col.id),
    [allColumns],
  );

  const groupedColumns = useMemo(() => {
    if (groupByCategory) {
      return groupByCategory(allColumnIds);
    }
    // Default: single "Other" group with all columns
    return [{ category: 'Other' as const, columns: allColumnIds }];
  }, [allColumnIds, groupByCategory]);

  // Handle drag end - reorder visible columns
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = visibleColumns.indexOf(active.id as string);
      const newIndex = visibleColumns.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(visibleColumns, oldIndex, newIndex);
        onReorderColumns(newOrder);
      }
    }
  };

  // Get display name for a column
  const getDisplayName = (columnId: string): string => {
    // Try custom function first
    if (customGetDisplayName) {
      const customName = customGetDisplayName(columnId);
      if (customName !== columnId) {
        return customName;
      }
    }

    // Try column header
    const column = columnMap.get(columnId);
    if (column && typeof column.header === 'string') {
      return column.header;
    }

    // Fall back to custom function result or ID
    return customGetDisplayName ? customGetDisplayName(columnId) : columnId;
  };

  // Count visible columns
  const visibleCount = visibleColumns.length;
  const totalCount = allColumns.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
        >
          <ViewColumnsIcon className="h-4 w-4 shrink-0" />
          {label}
          {hasChanges && (
            <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-blue-500" />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className={`${MENU_WIDTH} p-0 dark:border-zinc-700 dark:bg-zinc-800`}
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-200 px-2 py-1.5 dark:border-zinc-700">
          <span className="text-[11px] font-medium text-zinc-900 dark:text-white">
            {visibleCount} of {totalCount} columns
          </span>
          <button
            type="button"
            onClick={onReset}
            disabled={!hasChanges}
            className={cn(
              'inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium',
              'text-zinc-700 transition-colors hover:bg-zinc-100',
              'dark:text-zinc-300 dark:hover:bg-zinc-700',
              'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
            )}
          >
            <ArrowPathIcon className="h-3 w-3 shrink-0" />
            <span className="whitespace-nowrap">Reset</span>
          </button>
        </div>

        {/* Column List */}
        <div className={`${MAX_MENU_HEIGHT} overflow-y-auto p-1.5`}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            {/* Visible columns (sortable) */}
            {visibleColumns.length > 0 && (
              <div className="mb-2">
                <div className="mb-1 px-1">
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                    Visible ({visibleColumns.length})
                  </div>
                  <div className="text-[9px] text-zinc-400 dark:text-zinc-500">
                    Drag to reorder
                  </div>
                </div>
                <SortableContext
                  items={visibleColumns}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-0">
                    {visibleColumns.map((columnId) => (
                      <DraggableColumnItem
                        key={columnId}
                        id={columnId}
                        label={getDisplayName(columnId)}
                        isVisible
                        onToggle={() => onToggleColumn(columnId)}
                        isDraggable
                        disabled={visibleColumns.length === 1}
                      />
                    ))}
                  </div>
                </SortableContext>
              </div>
            )}

            {/* Hidden columns (grouped by category) */}
            {groupedColumns.map(({ category, columns }) => {
              const hiddenInCategory = columns.filter(
                (id) => !visibleColumns.includes(id),
              );
              if (hiddenInCategory.length === 0) return null;

              return (
                <div key={category} className="mb-2">
                  <div className="mb-1 px-1 text-[9px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                    {category}
                  </div>
                  <div className="space-y-0">
                    {hiddenInCategory.map((columnId) => (
                      <DraggableColumnItem
                        key={columnId}
                        id={columnId}
                        label={getDisplayName(columnId)}
                        isVisible={false}
                        onToggle={() => onToggleColumn(columnId)}
                        isDraggable={false}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </DndContext>
        </div>
      </PopoverContent>
    </Popover>
  );
};
